import { SystemConfiguration } from '../config.type';
import { NewUiZoneWidgetConfig, UiZoneWidgetConfig } from '../ui-system-config.v2.type';
import { BaseCssConfig, WorkflowConfig } from '../workflow.type';
import { WorkflowWidgetConfig, WorkflowZoneDevicesConfig } from '../workflow.v2.type';
import { Constants } from './workflow-constants.type';

/**
 * Drops css_config keys whose value strictly equals the shared default
 * (Constants.defaultBaseCssConfig). Inverse of hydrateBaseCssConfig — stored
 * widget configs only keep deliberate overrides.
 */
// fields typed as number that older stored configs hold as numeric strings
// (the font-size panel input historically propagated the raw DOM string)
const NUMERIC_CSS_FIELDS = ['fontSize', 'textInputFontSize'];

export function pruneBaseCssConfig(cssConfig: BaseCssConfig | undefined): BaseCssConfig {
  const defaults = Constants.defaultBaseCssConfig as unknown as Record<string, unknown>;
  const pruned: Record<string, unknown> = {};
  Object.entries(cssConfig ?? {}).forEach(([key, value]) => {
    if (NUMERIC_CSS_FIELDS.includes(key) && typeof value === 'string' && !isNaN(Number(value))) {
      value = Number(value);
    }
    if (value !== defaults[key] && value !== undefined) {
      pruned[key] = value;
    }
  });
  return pruned as unknown as BaseCssConfig;
}

/**
 * Restores the shared defaults under a (possibly pruned) stored css_config so
 * consumers always see a complete object. hydrate(prune(x)) === {...defaults, ...x}.
 * Explicit `undefined` values are dropped before the spread — some callers
 * (e.g. workflow.common.service.ts's global-styling reset) rebuild a css_config
 * from individual fields read off an already-pruned object, which can carry
 * `key: undefined` rather than an absent key; a plain spread would let that
 * undefined win over the default instead of falling through to it.
 * `undefined` in means "no css_config at all" (the widget doesn't use it) —
 * returned as-is rather than manufacturing a default set. Callers that always
 * need a complete object for computing styles (e.g. getSpecificBaseCssStyle)
 * pass `?? {}` themselves.
 */
export function hydrateBaseCssConfig(
  cssConfig: Partial<BaseCssConfig> | undefined,
): BaseCssConfig | undefined {
  if (cssConfig === undefined) return undefined;
  const defined = Object.fromEntries(Object.entries(cssConfig).filter(([, value]) => value !== undefined));
  return { ...Constants.defaultBaseCssConfig, ...defined };
}

/**
 * Restores full css_configs on a mapped (UI-side) widget and its
 * nested/child widgets. Stored/published system configs carry pruned css
 * (only overrides), but runtime consumers interpolate css fields unguarded,
 * so consumers hydrate at ingestion via hydrateSystemConfigCssConfigs.
 * Widgets/nested widgets with no css_config at all don't use it, so they're
 * left untouched rather than gaining the full default set.
 */
export function hydrateUiWidgetCssConfig(widget: UiZoneWidgetConfig): void {
  if (widget.attributes?.css_config !== undefined) {
    widget.attributes.css_config = hydrateBaseCssConfig(widget.attributes.css_config) as BaseCssConfig;
  }
  Object.values(widget.nestedWidgets ?? {}).forEach((nested) => {
    if (nested?.css_config !== undefined) {
      nested.css_config = hydrateBaseCssConfig(nested.css_config) as BaseCssConfig;
    }
  });
  (widget as NewUiZoneWidgetConfig).childComponents?.flat().forEach((child) => {
    if (child) hydrateUiWidgetCssConfig(child);
  });
}

/**
 * Hydrates every widget css_config in a SystemConfiguration, in place.
 * Call where a system config enters an app (web frontend load, builder's
 * local mapping) — published configs stay pruned.
 */
export function hydrateSystemConfigCssConfigs<T extends SystemConfiguration>(config: T): T {
  Object.values(config?.ui?.zones ?? {}).forEach((zone) =>
    Object.values(zone?.roles ?? {}).forEach((roleCanvas) =>
      Object.values(roleCanvas ?? {}).forEach((devices) =>
        Object.values(devices ?? {}).forEach((zoneConfig) =>
          zoneConfig?.pages?.forEach((page) => {
            page.widgetLayouts?.forEach(hydrateUiWidgetCssConfig);
            page.footerWidgetLayouts?.forEach(hydrateUiWidgetCssConfig);
          }),
        ),
      ),
    ),
  );
  return config;
}

function pruneWidgetTree(widget: WorkflowWidgetConfig): number {
  let removed = 0;

  const prune = (holder: { css_config?: BaseCssConfig } | undefined) => {
    if (!holder?.css_config) return;
    const before = Object.keys(holder.css_config).length;
    holder.css_config = pruneBaseCssConfig(holder.css_config);
    removed += before - Object.keys(holder.css_config).length;
  };

  prune(widget.widgetConfiguration?.attributes);
  Object.values(widget.widgetConfiguration?.nestedWidgets ?? {}).forEach((nested) =>
    prune(nested?.attributes),
  );
  widget.childComponents?.flat().forEach((child) => {
    if (child) removed += pruneWidgetTree(child);
  });

  return removed;
}

/**
 * One-shot bulk version of the prune-on-save behaviour: strips redundant
 * default css_config values from every widget on every role/zone/device/page.
 * Mutates the config in place and returns the number of keys removed.
 */
export function pruneWorkflowWidgetCssConfigs(config: WorkflowConfig): number {
  let removed = 0;

  config.permissionSections?.roles?.forEach((role) => {
    Object.values(role.zones ?? {}).forEach((canvasZone) => {
      Object.values(canvasZone?.canvases?.subscription ?? {}).forEach((devices) => {
        if (!devices) return;
        Object.values(devices as WorkflowZoneDevicesConfig).forEach((zoneConfig) => {
          zoneConfig?.pages?.forEach((page) => {
            page.widgets?.forEach((widget) => (removed += pruneWidgetTree(widget)));
            page.footerWidgets?.forEach((widget) => (removed += pruneWidgetTree(widget)));
          });
        });
      });
    });
  });

  return removed;
}
